import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Shield, CreditCard, CheckCircle, ArrowRight, SpinnerGap } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-hub-623/artifacts/04kw0jh0_IMG_4683.jpeg';

export const SubscribePage = () => {
  const { user, isSubscribed, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(
        `${API}/api/payments/create-checkout`,
        { origin_url: originUrl },
      );
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setLoading(false);
    }
  };

  // If already subscribed, redirect to dashboard
  if (isSubscribed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <div className="w-full max-w-md text-center">
          <CheckCircle weight="duotone" className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Already Subscribed</h1>
          <p className="text-muted-foreground mb-6">You have an active subscription.</p>
          <Link to="/dashboard">
            <Button className="rounded-sm" data-testid="go-to-dashboard-btn">
              Go to Dashboard
              <ArrowRight weight="bold" className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const benefits = [
    'Full access to DVSA sanctions database',
    'Search by offence, category, or severity',
    'Filter by vehicle type (HGV, PSV, LGV)',
    'View detailed enforcement outcomes',
    'Mobile-friendly interface',
    'Cancel anytime'
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img src={LOGO_URL} alt="Vantage" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-tight">Vantage Fleet</span>
          </Link>
        </div>
        
        {/* Subscribe Card */}
        <div className="border border-border bg-background">
          <div className="p-8 border-b border-border">
            <h1 className="text-2xl font-semibold tracking-tight mb-2">Subscribe to Continue</h1>
            <p className="text-muted-foreground">Get full access to the DVSA enforcement sanctions database</p>
          </div>
          
          <div className="p-8 border-b border-border">
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-4xl font-bold">£0.99</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            
            <ul className="space-y-3">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle weight="duotone" className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="p-8">
            <Button 
              onClick={handleSubscribe}
              className="w-full rounded-sm h-12 text-base"
              disabled={loading}
              data-testid="subscribe-btn"
            >
              {loading ? (
                <>
                  <SpinnerGap weight="bold" className="mr-2 h-5 w-5 animate-spin" />
                  Redirecting to checkout...
                </>
              ) : (
                <>
                  <CreditCard weight="duotone" className="mr-2 h-5 w-5" />
                  Subscribe Now
                </>
              )}
            </Button>
            
            <p className="text-xs text-muted-foreground text-center mt-4">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </div>
        </div>
        
        {user && (
          <p className="text-sm text-muted-foreground text-center mt-6">
            Logged in as {user.email}
          </p>
        )}
      </div>
    </div>
  );
};
